import { z } from 'zod';
import { isNil } from 'lodash';

// Define the allowed report types based on the actual routes
const ReportTypeSchema = z.enum([
  'query',
  'funnel',
  'retention',
  'prediction',
  'path',
  'data-table',
  'significance',
]);

// Create regex patterns from the route patterns
const embedReportPattern = new RegExp(`^/embed/report/([^/]+)/([^/]+)$`);
const embedDashboardPattern = new RegExp(`^/embed/dash/([^/]+)$`);
const embedDashboardWithInstancePattern = new RegExp(
  `^/embed/dash/([^/]+)/([^/]+)$`,
);

const isNumericString = (value: string) => /^\d+$/.test(value);

// Define the URL patterns we want to allow
const EmbedReportUrlSchema = z.string().refine(
  (url) => {
    try {
      const decodedUrl = decodeURIComponent(url);

      // Do not allow external url
      if (decodedUrl.includes('://') || decodedUrl.startsWith('//')) {
        return false;
      }

      // Remove query params and fragments
      const cleanUrl = decodedUrl.split('?')[0].split('#')[0];

      // Check if URL matches the pattern
      if (!embedReportPattern.test(cleanUrl)) {
        return false;
      }

      // Split the path into parts
      const parts = cleanUrl.split('/').filter(Boolean);

      // Validate report type
      const reportType = parts[2];
      const result = ReportTypeSchema.safeParse(reportType);
      if (!result.success) {
        return false;
      }

      // Validate reportId is a non-empty numeric string
      const reportId = parts[3];
      if (!reportId || !isNumericString(reportId)) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  },
  { message: 'Invalid embed report URL format' },
);

const EmbedDashboardUrlSchema = z.string().refine(
  (url) => {
    try {
      const decodedUrl = decodeURIComponent(url);
      if (decodedUrl.includes('://') || decodedUrl.startsWith('//')) {
        return false;
      }

      // Remove query params and fragments
      const cleanUrl = decodedUrl.split('?')[0].split('#')[0];

      // Check if URL matches either dashboard pattern
      if (
        !embedDashboardPattern.test(cleanUrl) &&
        !embedDashboardWithInstancePattern.test(cleanUrl)
      ) {
        return false;
      }

      // Split the path into parts
      const parts = cleanUrl.split('/').filter(Boolean);

      // Validate dashboardBaseId is a non-empty numeric string
      const dashboardBaseId = parts[2];
      if (!dashboardBaseId || !isNumericString(dashboardBaseId)) {
        return false;
      }

      // If instanceId exists, validate it's a numeric string
      if (parts[3] && !isNumericString(parts[3])) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  },
  { message: 'Invalid embed dashboard URL format' },
);

// Combine both schemas
const ValidRedirectUrlSchema = z.union([
  EmbedReportUrlSchema,
  EmbedDashboardUrlSchema,
]);

/**
 * Validates and sanitizes a redirect URL to ensure it's a safe internal path
 * Only allows URLs that match the patterns defined in EmbedRoutes
 * @param url The URL to validate
 * @returns The sanitized URL or null if invalid
 */
export const validateRedirectUrl = (url: string | null): string | null => {
  if (isNil(url)) {
    return null;
  }

  try {
    const result = ValidRedirectUrlSchema.safeParse(url);
    if (!result.success) {
      console.error('Invalid redirect URL:', result.error);
      return null;
    }

    // Return the validated URL
    return result.data;
  } catch (error) {
    console.error('Error validating redirect URL:', error);
    return null;
  }
};
